/**
 * 認証ストア (§7)。Google サインイン + allowlist 照合。
 *
 * status の 5値:
 *   loading    … onAuthStateChanged の初回発火待ち
 *   signed-out … 未ログイン
 *   unverified … Google アカウントの email 未検証 (会社メール等で false になり得る)。
 *                ルール上 allowlist read も拒否されるため、判定は allowlist 照会より先。
 *   denied     … allowlist 未登録
 *   ready      … 利用可
 *
 * Firebase 無効モード (VITE_FIREBASE_PROJECT_ID 未設定) は即 ready / email='local@dev' —
 * 開発サンドボックス・既存テスト・プレビュー検証を現行どおり通すための分岐。
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { getFirebase, isFirebaseConfigured } from '@/lib/firebase/init';
import { normalizedEmail, isCompanyDomain } from '@/lib/firebase/email';

export type AuthStatus = 'loading' | 'signed-out' | 'unverified' | 'denied' | 'ready';

export const useAuthStore = defineStore('auth', () => {
  const status = ref<AuthStatus>('loading');
  const email = ref<string | null>(null);
  /** Google アカウントの表示名 (§7 監査フィールド updatedByName の元)。ready のときのみ保持。
   * Firebase 無効モードでは常に null。 */
  const displayName = ref<string | null>(null);
  const error = ref<string | null>(null);

  let settled: Promise<void>;
  let markSettled: () => void = () => {};

  function newSettleGate() {
    settled = new Promise<void>((resolve) => {
      markSettled = resolve;
    });
  }
  newSettleGate();

  // onAuthStateChanged が短時間に複数回発火すると evaluateUser の呼び出しも複数
  // 並行に進みうる。getDoc の解決順は呼び出し順と一致しないため、世代 ID で
  // 「自分が最新の呼び出しか」を判定し、古い呼び出しの結果で新しい状態を
  // 上書きしないようにする。
  let generation = 0;

  async function evaluateUser(user: {
    email: string | null;
    emailVerified: boolean;
    displayName?: string | null;
  } | null): Promise<void> {
    const myGeneration = ++generation;
    const isCurrent = () => myGeneration === generation;

    if (!user || !user.email) {
      if (isCurrent()) {
        status.value = 'signed-out';
        email.value = null;
        displayName.value = null;
      }
      return;
    }
    if (!user.emailVerified) {
      if (isCurrent()) {
        status.value = 'unverified';
        email.value = null;
        displayName.value = null;
      }
      return;
    }
    const normalized = normalizedEmail(user.email);
    if (isCompanyDomain(normalized)) {
      // 社内ドメインは firestore.rules の isCompanyDomain() 側でも自動許可されるため、
      // allowlist の getDoc を発行せずに即 ready へ進む (read課金の削減にもなる)。
      if (isCurrent()) {
        email.value = normalized;
        displayName.value = user.displayName ?? null;
        status.value = 'ready';
      }
      return;
    }
    try {
      const { db } = await getFirebase();
      const { doc, getDoc } = await import('firebase/firestore');
      const snap = await getDoc(doc(db, 'allowlist', normalized));
      if (!isCurrent()) return;
      if (snap.exists()) {
        email.value = normalized;
        displayName.value = user.displayName ?? null;
        status.value = 'ready';
      } else {
        email.value = null;
        displayName.value = null;
        status.value = 'denied';
      }
    } catch (e) {
      if (!isCurrent()) return;
      error.value = e instanceof Error ? e.message : String(e);
      status.value = 'denied';
      displayName.value = null;
    }
  }

  async function init(): Promise<void> {
    try {
      if (!isFirebaseConfigured()) {
        status.value = 'ready';
        email.value = 'local@dev';
        markSettled();
        return;
      }
      const { auth } = await getFirebase();
      const { onAuthStateChanged } = await import('firebase/auth');
      onAuthStateChanged(auth, (user) => {
        void evaluateUser(user).finally(markSettled);
      });
    } catch (e) {
      // init 自体が失敗すると status='loading' のまま whenSettled() が永遠に
      // 解決しなくなるため、失敗も必ず解決させて呼び出し元をハングさせない。
      error.value = e instanceof Error ? e.message : String(e);
      status.value = 'denied';
      markSettled();
    }
  }

  async function signInWithGoogle(): Promise<void> {
    const { auth } = await getFirebase();
    const { GoogleAuthProvider, signInWithPopup } = await import('firebase/auth');
    const provider = new GoogleAuthProvider();
    // hd (hosted domain) はアカウント選択で grd.inc を優先表示させる UX ヒントに過ぎず、
    // セキュリティ境界ではない (ユーザーは別ドメインのアカウントを選択できてしまう)。
    // 実際の許可判定は firestore.rules (isCompanyDomain()/allowlist) が担う。
    provider.setCustomParameters({ hd: 'grd.inc' });
    error.value = null;
    try {
      await signInWithPopup(auth, provider);
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'サインインに失敗しました';
    }
  }

  async function signOutUser(): Promise<void> {
    // in-flight な evaluateUser (onAuthStateChanged の既発火分) が isCurrent() を
    // 満たさなくなるよう先に世代を進める。これをしないと、signOut() 完了後に
    // 古い呼び出しの結果 (例: 直前の allowlist 照会) が status/email を上書きし、
    // 下の明示的な 'signed-out' 設定を巻き戻してしまい得る。
    generation++;
    const { auth } = await getFirebase();
    const { signOut } = await import('firebase/auth');
    await signOut(auth);
    status.value = 'signed-out';
    email.value = null;
    displayName.value = null;
  }

  function whenSettled(): Promise<void> {
    return settled;
  }

  void init();

  return { status, email, displayName, error, whenSettled, signInWithGoogle, signOutUser };
});
